import { useState, useRef, useCallback, useEffect } from "react";
import type { SlashCommandType, SlashCommandResult, EntityChip } from "../types/index.js";
import { apiGet } from "../lib/api.js";

/** Minimum debounce interval for search requests (ms). */
const DEBOUNCE_MS = 300;

/** Slash command prefix map — maps the typed prefix to the entity type. */
const SLASH_PREFIXES: Record<string, SlashCommandType> = {
	"/u ": "user",
	"/t ": "task",
	"/m ": "milestone",
	"/s ": "status",
};

interface SlashCommandState {
	/** Whether the autocomplete dropdown is open. */
	isOpen: boolean;
	/** The type of entity being searched. */
	activeType: SlashCommandType | null;
	/** The raw query text after the slash prefix. */
	query: string;
	/** Start index of the slash command in the input text. */
	commandStart: number;
}

export interface UseSlashCommandsReturn {
	/** Search results for the current slash command query. */
	results: SlashCommandResult[];
	/** Whether the autocomplete dropdown should be visible. */
	isOpen: boolean;
	/**
	 * Whether to show the command hint panel.
	 * True when the user has typed a bare `/` without a recognised prefix yet.
	 */
	showCommandHint: boolean;
	/** Index of the currently highlighted result (keyboard navigation). */
	activeIndex: number;
	/** Entity chips that the user has selected. */
	selectedChips: EntityChip[];
	/** Whether a search request is in flight. */
	isLoading: boolean;
	/** The active slash command type, if any. */
	activeType: SlashCommandType | null;
	/**
	 * Whether the ClientUserForm modal should be visible.
	 * Set to true when the user types `/c ` (slash c space).
	 */
	showClientForm: boolean;
	/**
	 * Whether the TaskCreateForm modal should be visible.
	 * Set to true when the user types `/t` (bare) or `/t ` with a 500ms debounce.
	 */
	showTaskForm: boolean;
	/**
	 * Process the current input text to detect slash commands.
	 * Call this from onChange with the full input value and cursor position.
	 */
	processInput: (inputValue: string, cursorPosition: number) => void;
	/**
	 * Handle keyboard events for the dropdown (arrow keys, enter, escape).
	 * Returns true if the event was consumed by the slash command system.
	 */
	handleKeyDown: (e: React.KeyboardEvent) => boolean;
	/** Select a result from the dropdown — returns the updated input text. */
	selectResult: (result: SlashCommandResult, currentText: string) => string;
	/** Remove a chip by its ID. */
	removeChip: (chipId: string) => void;
	/** Reset all slash command state (e.g. after sending a message). */
	resetSlashState: () => void;
	/** Close the ClientUserForm modal and reset showClientForm to false. */
	closeClientForm: () => void;
	/** Close the TaskCreateForm modal and reset showTaskForm to false. */
	closeTaskForm: () => void;
}

/**
 * Hook for slash command autocomplete in the chat input.
 *
 * Detects `/u `, `/t `, `/m `, and `/s ` prefixes, searches the backend for
 * matching entities, and manages chip selection and keyboard navigation.
 *
 * Also exposes `showCommandHint` which is true when the user has typed a
 * bare `/` — before any recognised prefix — so the UI can display a short
 * helper listing the available commands.
 *
 * Exposes `showTaskForm` which is true when the user types `/t` (bare or
 * followed by a space) for 500ms without further typing — opening the
 * TaskCreateForm modal.
 */
export function useSlashCommands(projectId: string | null): UseSlashCommandsReturn {
	const [results, setResults] = useState<SlashCommandResult[]>([]);
	const [isOpen, setIsOpen] = useState(false);
	/** True when the user has typed a bare `/` but no recognised prefix yet. */
	const [showCommandHint, setShowCommandHint] = useState(false);
	const [activeIndex, setActiveIndex] = useState(0);
	const [selectedChips, setSelectedChips] = useState<EntityChip[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	// Promoted to state so consumers re-render when activeType changes.
	const [activeType, setActiveType] = useState<SlashCommandType | null>(null);
	/** True when the user has typed `/c ` — triggers the ClientUserForm modal. */
	const [showClientForm, setShowClientForm] = useState(false);
	/** True when the user types `/t` (bare) or `/t ` — triggers the TaskCreateForm modal. */
	const [showTaskForm, setShowTaskForm] = useState(false);
	/** Debounce timer for the task form trigger. */
	const taskFormTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const slashStateRef = useRef<SlashCommandState>({
		isOpen: false,
		activeType: null,
		query: "",
		commandStart: 0,
	});

	const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	/** Abort controller for in-flight search requests. */
	const abortControllerRef = useRef<AbortController | null>(null);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
			if (taskFormTimerRef.current) clearTimeout(taskFormTimerRef.current);
			abortControllerRef.current?.abort();
		};
	}, []);

	const closeDropdown = useCallback(() => {
		setIsOpen(false);
		setShowCommandHint(false);
		setResults([]);
		setActiveIndex(0);
		setActiveType(null);
		slashStateRef.current = {
			isOpen: false,
			activeType: null,
			query: "",
			commandStart: 0,
		};
	}, []);

	const searchEntities = useCallback(
		async (type: SlashCommandType, query: string, listMode = false) => {
			if (!projectId || (query.length === 0 && !listMode)) {
				setResults([]);
				setIsLoading(false);
				return;
			}

			// Abort any previous in-flight request
			abortControllerRef.current?.abort();
			const controller = new AbortController();
			abortControllerRef.current = controller;

			setIsLoading(true);

			try {
				const encodedQuery = encodeURIComponent(query);
				const response = await apiGet(
					`/api/projects/${projectId}/search-entities?type=${type}&q=${encodedQuery}`,
					controller.signal,
				);

				if (controller.signal.aborted) return;

				if (response.ok) {
					const data = (await response.json()) as { results: SlashCommandResult[] };
					setResults(data.results);
					setActiveIndex(0);
				} else {
					setResults([]);
				}
			} catch {
				if (!controller.signal.aborted) {
					setResults([]);
				}
			} finally {
				if (!controller.signal.aborted) {
					setIsLoading(false);
				}
			}
		},
		[projectId],
	);

	const processInput = useCallback(
		(inputValue: string, cursorPosition: number) => {
			// Look backwards from the cursor to find a slash command prefix
			const textUpToCursor = inputValue.slice(0, cursorPosition);

			// Detect the /c command (modal form, not an inline dropdown).
			// The prefix must appear at the start of text or after whitespace/newline.
			const clientFormPattern = /(^|[\s\n])\/c $/;
			if (clientFormPattern.test(textUpToCursor)) {
				// /c followed by a space — open the client user form modal.
				// Do NOT open the dropdown; the form is a separate modal UI.
				if (taskFormTimerRef.current) clearTimeout(taskFormTimerRef.current);
				setShowClientForm(true);
				setIsOpen(false);
				setShowCommandHint(false);
				setResults([]);
				setActiveIndex(0);
				setActiveType(null);
				slashStateRef.current = {
					isOpen: false,
					activeType: null,
					query: "",
					commandStart: 0,
				};
				return;
			}

			// Detect the /t command (bare or followed by a single space, nothing more).
			// After a 500ms debounce with no further typing, open the TaskCreateForm modal.
			// Pattern: `/t` at start of input or preceded by whitespace, optionally + single space.
			const taskFormPattern = /(^|[\s\n])\/t ?$/;
			if (taskFormPattern.test(textUpToCursor)) {
				// Cancel any existing task form debounce and restart it
				if (taskFormTimerRef.current) clearTimeout(taskFormTimerRef.current);
				taskFormTimerRef.current = setTimeout(() => {
					setShowTaskForm(true);
				}, 500);
				setIsOpen(false);
				setShowCommandHint(false);
				setResults([]);
				setActiveIndex(0);
				setActiveType(null);
				slashStateRef.current = {
					isOpen: false,
					activeType: null,
					query: "",
					commandStart: 0,
				};
				return;
			} else {
				// User typed past /t (e.g. /t somequery) — cancel the task form debounce
				if (taskFormTimerRef.current) {
					clearTimeout(taskFormTimerRef.current);
					taskFormTimerRef.current = null;
				}
				setShowTaskForm(false);
			}

			// Detect `/m?` and `/s?` — list mode: show all milestones or statuses.
			// Must be checked BEFORE the SLASH_PREFIXES loop to avoid conflicting
			// with the `/m ` prefix.  Pattern requires the `?` to be at the cursor
			// (i.e. the last character typed) and the slash to be at the start of
			// the input or preceded by whitespace/newline.
			const listModePattern = /(^|[\s\n])\/([ms])\?$/;
			const listModeMatch = listModePattern.exec(textUpToCursor);
			if (listModeMatch) {
				const listType: SlashCommandType = listModeMatch[2] === "m" ? "milestone" : "status";
				// Find the commandStart — position of the `/` in the full input
				const matchIndex = listModeMatch.index + listModeMatch[1].length;

				slashStateRef.current = {
					isOpen: true,
					activeType: listType,
					query: "",
					commandStart: matchIndex,
				};
				setActiveType(listType);
				setShowCommandHint(false);
				setIsOpen(true);

				// Skip debounce — the user has explicitly requested the full list.
				// searchEntities handles aborting any in-flight request internally.
				if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
				searchEntities(listType, "", true);
				return;
			}

			let foundType: SlashCommandType | null = null;
			let foundStart = -1;
			let foundQuery = "";

			for (const [prefix, type] of Object.entries(SLASH_PREFIXES)) {
				// Find the last occurrence of this prefix before the cursor
				const lastIndex = textUpToCursor.lastIndexOf(prefix);
				if (lastIndex === -1) continue;

				// The prefix must be at the start of the text or preceded by whitespace
				if (lastIndex > 0 && textUpToCursor[lastIndex - 1] !== " " && textUpToCursor[lastIndex - 1] !== "\n") {
					continue;
				}

				// Extract query: text between end of prefix and cursor
				const queryStart = lastIndex + prefix.length;
				const query = textUpToCursor.slice(queryStart);

				// Query must not contain another slash command or newline
				if (
					query.includes("\n") ||
					query.includes("/u ") ||
					query.includes("/t ") ||
					query.includes("/m ") ||
					query.includes("/s ")
				) {
					continue;
				}

				// Prefer the most recent (rightmost) match
				if (lastIndex > foundStart) {
					foundType = type;
					foundStart = lastIndex;
					foundQuery = query;
				}
			}

			if (foundType && foundStart >= 0) {
				// Update the ref with latest command state (used by selectResult)
				slashStateRef.current = {
					isOpen: foundQuery.length >= 1,
					activeType: foundType,
					query: foundQuery,
					commandStart: foundStart,
				};
				setActiveType(foundType);
				// A recognised prefix is active — hide the bare-slash hint
				setShowCommandHint(false);

				// Only open the dropdown once the user has typed at least one character
				// after the prefix — avoids a premature "No results" flash.
				if (foundQuery.length >= 1) {
					setIsOpen(true);

					// Debounce the search
					if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
					debounceTimerRef.current = setTimeout(() => {
						searchEntities(foundType, foundQuery);
					}, DEBOUNCE_MS);
				} else {
					// Prefix detected but no query yet — keep dropdown closed
					setIsOpen(false);
					setResults([]);
				}
			} else {
				// Check whether the cursor is immediately after a bare `/` (no recognised
				// prefix matched yet). Show the command hint panel in this case.
				// Pattern: `/` must be at start of input or preceded by whitespace/newline.
				const bareSlashPattern = /(^|[\s\n])\/$/;
				const showHint = bareSlashPattern.test(textUpToCursor);
				setShowCommandHint(showHint);

				// Always reset dropdown results state
				setIsOpen(false);
				setResults([]);
				setActiveIndex(0);
				setActiveType(null);
				slashStateRef.current = {
					isOpen: false,
					activeType: null,
					query: "",
					commandStart: 0,
				};
			}
		},
		[searchEntities],
	);

	const selectResult = useCallback(
		(result: SlashCommandResult, currentText: string): string => {
			const state = slashStateRef.current;
			if (!state.activeType) return currentText;

			// Add the chip
			const chip: EntityChip = {
				type: result.type,
				id: result.id,
				label: result.label,
			};

			// Avoid duplicate chips
			setSelectedChips((prev) => {
				if (prev.some((c) => c.id === chip.id && c.type === chip.type)) return prev;
				return [...prev, chip];
			});

			// Map each type to its prefix and token format
			const PREFIX_MAP: Record<SlashCommandType, string> = {
				user: "/u ",
				task: "/t ",
				milestone: "/m ",
				status: "/s ",
			};

			// Token format: user → [@Name:ID], task → [#Name:ID], milestone → [!Name:ID], status → [$Name:ID]
			const TOKEN_PREFIX_MAP: Record<SlashCommandType, string> = {
				user: "@",
				task: "#",
				milestone: "!",
				status: "$",
			};

			const prefix = PREFIX_MAP[state.activeType];
			const tokenPrefix = TOKEN_PREFIX_MAP[state.activeType];
			const commandEnd = state.commandStart + prefix.length + state.query.length;
			const before = currentText.slice(0, state.commandStart);
			const after = currentText.slice(commandEnd);

			// Insert a structured token reference in the text.
			// The ID is prefixed with the entity type so parseEntityTokens can
			// distinguish between users, tasks, milestones, and statuses.
			const reference = `[${tokenPrefix}${result.label}:${state.activeType}_${result.id}]`;
			const newText = before + reference + (after.startsWith(" ") ? after : " " + after);

			closeDropdown();
			return newText;
		},
		[closeDropdown],
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent): boolean => {
			// Dismiss hint panel on Escape even when the dropdown is closed
			if (!isOpen && showCommandHint) {
				if (e.key === "Escape") {
					e.preventDefault();
					closeDropdown();
					return true;
				}
				return false;
			}

			if (!isOpen) return false;

			switch (e.key) {
				case "ArrowDown": {
					e.preventDefault();
					setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
					return true;
				}
				case "ArrowUp": {
					e.preventDefault();
					setActiveIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
					return true;
				}
				case "Enter": {
					if (results.length > 0 && activeIndex >= 0 && activeIndex < results.length) {
						e.preventDefault();
						// Return true to signal the event was consumed.
						// The actual selection is handled by the component via selectResult.
						return true;
					}
					return false;
				}
				case "Escape": {
					e.preventDefault();
					closeDropdown();
					return true;
				}
				case "Tab": {
					if (results.length > 0 && activeIndex >= 0 && activeIndex < results.length) {
						e.preventDefault();
						return true;
					}
					return false;
				}
				default:
					return false;
			}
		},
		[isOpen, showCommandHint, results, activeIndex, closeDropdown],
	);

	const removeChip = useCallback((chipId: string) => {
		setSelectedChips((prev) => prev.filter((c) => c.id !== chipId));
	}, []);

	const closeClientForm = useCallback(() => {
		setShowClientForm(false);
	}, []);

	const closeTaskForm = useCallback(() => {
		setShowTaskForm(false);
	}, []);

	const resetSlashState = useCallback(() => {
		closeDropdown();
		setSelectedChips([]);
		setShowClientForm(false);
		setShowTaskForm(false);
		if (taskFormTimerRef.current) {
			clearTimeout(taskFormTimerRef.current);
			taskFormTimerRef.current = null;
		}
	}, [closeDropdown]);

	return {
		results,
		isOpen,
		showCommandHint,
		activeIndex,
		selectedChips,
		isLoading,
		activeType,
		showClientForm,
		showTaskForm,
		processInput,
		handleKeyDown,
		selectResult,
		removeChip,
		resetSlashState,
		closeClientForm,
		closeTaskForm,
	};
}
